import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { ChevronRight12Regular } from '@fluentui/react-icons';
import { StatePill } from '../../components/StatePill';
import { vt } from '../../theme';
import type { FlowRow, FlowState } from './model';

/** A stable marker beside the Griffel class, so a test (or a user stylesheet) can find denied rows. */
export const DENIED_ROW_CLASS = 'flow-denied';

const useStyles = makeStyles({
  table: { tableLayout: 'fixed', width: '100%' },
  head: {
    height: '30px',
    fontSize: '11.5px',
    color: vt.fg3,
    fontWeight: tokens.fontWeightRegular,
    borderBottom: `1px solid ${vt.surface2}`,
  },
  row: { height: '34px', borderBottom: `1px solid ${vt.surface2}` },
  // A denied flow is tinted across the whole row: the one thing here to spot without reading.
  denied: { backgroundColor: vt.tintRed, ':hover': { backgroundColor: vt.tintRed } },
  cell: { paddingBlock: 0, boxSizing: 'border-box', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' },
  end: { textAlign: 'end', '& > *': { justifyContent: 'flex-end' } },
  dot: { display: 'block', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: vt.fg4, marginInline: 'auto' },
  dotLive: { backgroundColor: vt.cyan, boxShadow: `0 0 0 3px ${vt.tintCyan}` },
  dotDenied: { backgroundColor: vt.red },
  endpoint: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg1 },
  chevron: { display: 'block', color: vt.fg4, marginInline: 'auto' },
  chevronDenied: { color: vt.red },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11.5px', color: vt.fg2 },
  muted: { color: vt.fg3 },
  ruleNumber: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11px', color: vt.fg4, marginRight: '8px' },
  ruleText: { fontSize: '11px', color: vt.fg3 },
  ruleDenied: { color: vt.red },
  empty: { color: vt.fg3, textAlign: 'center', height: '48px' },
});

const COLS: { key: string; label: string; width: string; end?: boolean }[] = [
  { key: 'state', label: '', width: '26px' },
  { key: 'source', label: 'Source', width: 'auto' },
  { key: 'arrow', label: '', width: '24px' },
  { key: 'destination', label: 'Destination', width: 'auto' },
  { key: 'protocol', label: 'Protocol', width: '72px' },
  { key: 'port', label: 'Port', width: '64px' },
  { key: 'bytes', label: 'Bytes', width: '84px', end: true },
  { key: 'duration', label: 'Duration', width: '84px', end: true },
  { key: 'rule', label: 'Matched rule', width: '200px' },
  { key: 'result', label: 'Result', width: '96px', end: true },
];

const PILL: Record<FlowState, { state: 'live' | 'allow' | 'deny'; label: string }> = {
  live: { state: 'live', label: 'Live' },
  allowed: { state: 'allow', label: 'Allowed' },
  denied: { state: 'deny', label: 'Denied' },
};

/** Mockup 06's flow log: source › destination, what it carried, and the rule that decided it. */
export function FlowTable({ rows }: { rows: FlowRow[] }) {
  const s = useStyles();
  return (
    <Table className={s.table} size="small" aria-label="Flows">
      <TableHeader>
        <TableRow className={s.head}>
          {COLS.map((c) => (
            <TableHeaderCell
              key={c.key}
              className={mergeClasses(s.cell, c.end && s.end)}
              style={{ width: c.width }}
              aria-label={c.key === 'state' ? 'State' : c.key === 'arrow' ? 'To' : undefined}
            >
              {c.label}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell className={s.empty} colSpan={COLS.length}>
              No flows match in the last 24 h
            </TableCell>
          </TableRow>
        )}
        {rows.map((r) => {
          const denied = r.state === 'denied';
          const pill = PILL[r.state];
          return (
            <TableRow
              key={r.id}
              className={mergeClasses(s.row, denied && s.denied, denied && DENIED_ROW_CLASS)}
              data-flow={r.id}
              data-state={r.state}
            >
              <TableCell className={s.cell}>
                <span
                  className={mergeClasses(s.dot, r.state === 'live' && s.dotLive, denied && s.dotDenied)}
                  aria-label={pill.label}
                  role="img"
                />
              </TableCell>
              <TableCell className={mergeClasses(s.cell, s.endpoint)} title={r.sourceIp}>
                {r.source}
              </TableCell>
              <TableCell className={s.cell}>
                <ChevronRight12Regular className={mergeClasses(s.chevron, denied && s.chevronDenied)} aria-hidden />
              </TableCell>
              <TableCell className={mergeClasses(s.cell, s.endpoint)} title={r.destinationIp}>
                {r.destination}
              </TableCell>
              <TableCell className={mergeClasses(s.cell, s.mono)}>{r.protocol}</TableCell>
              <TableCell className={mergeClasses(s.cell, s.mono)}>{r.port}</TableCell>
              <TableCell className={mergeClasses(s.cell, s.mono, s.end)}>{r.bytes}</TableCell>
              <TableCell className={mergeClasses(s.cell, s.mono, s.muted, s.end)}>{r.duration}</TableCell>
              <TableCell className={s.cell} data-testid="rule">
                {r.ruleNumber && <span className={s.ruleNumber}>{r.ruleNumber}</span>}
                <span className={mergeClasses(s.ruleText, denied && s.ruleDenied)}>{r.ruleText}</span>
              </TableCell>
              <TableCell className={mergeClasses(s.cell, s.end)}>
                <StatePill state={pill.state} label={pill.label} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
